import { produce } from 'immer'
import {
  GET_MONTHLY_SPEND_FAILURE,
  GET_MONTHLY_SPEND_INIT,
  GET_MONTHLY_SPEND_SUCCESS,
  GET_ORG_STATS_FAILURE,
  GET_ORG_STATS_INIT,
  GET_ORG_STATS_SUCCESS,
  GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE,
  GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT,
  GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS,
  GET_SIX_MONTH_SPEND_FAILURE,
  GET_SIX_MONTH_SPEND_INIT,
  GET_SIX_MONTH_SPEND_SUCCESS,
  GET_TOTAL_AVAILABLE_CREDIT_FAILURE,
  GET_TOTAL_AVAILABLE_CREDIT_INIT,
  GET_TOTAL_AVAILABLE_CREDIT_SUCCESS,
  GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE,
  GET_TOTAL_AVAILABLE_CREDIT_V2_INIT,
  GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS,
  GetMonthlySpendActions,
  GetOrgStatsActions,
  GetSixMonthSpendActions,
  GetSixMonthSpendByCurrencyActions,
  GetTotalAvailableCreditActions,
  GetTotalAvailableCreditV2Actions,
} from 'src/app/Home/redux/home.actions'
import { AlertsResponse } from 'src/models/alerts.models'
import { MonthlySpendDataResponse } from 'src/models/monthlySpendData.models'
import { NeedsApprovalResponse } from 'src/models/needsApproval.models'
import { homeRouteProps } from 'src/navigation/routeProps'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import TransactionsSnapshotPaginatedDataReducer, {
  Actions as TransactionsSnapshotPaginatedDataActions,
  initialState as initialTransactionsSnapshotPaginatedDataState,
  TransactionsSnapshotPaginatedDataState,
} from 'src/redux/transactionsSnapshotPaginatedData.reducer'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface HomeState {
  alerts: AlertsResponse & {
    loading: boolean
  }
  needsApproval: NeedsApprovalResponse & {
    loading: boolean
    complete: boolean
  }
  totalAvailableCredit: {
    loading: boolean
    totalAvailableCreditCents?: number
    totalAvailableCreditCentsUSD?: number | null
    totalAvailableCreditCentsCAD?: number | null
  }
  monthlySpend: {
    loading: boolean
    dates: string[]
    organizationId?: string
    topLevelError?: string
  }
  sixMonthSpend: {
    loading: boolean
    organizationId?: string
    lastUpdatedAt?: string
    monthSpendData: MonthlySpendDataResponse[]
    monthSpendDataUSD: MonthlySpendDataResponse[] | null
    monthSpendDataCAD: MonthlySpendDataResponse[] | null
    topLevelError?: string
  }
  orgStats: {
    loading: boolean
    total?: number
    owner?: number
    admin?: number
    cardManager?: number
    bookkeeper?: number
    member?: number
    guest?: number
    topLevelError?: string
  }
  transactionsSnapshotPaginatedData: TransactionsSnapshotPaginatedDataState
}

export const initialState: HomeState = {
  alerts: {
    loading: false,
  },
  needsApproval: {
    loading: false,
    complete: false,
  },
  totalAvailableCredit: {
    loading: false,
  },
  monthlySpend: {
    loading: false,
    dates: [],
  },
  sixMonthSpend: {
    loading: false,
    monthSpendData: [],
    monthSpendDataUSD: [],
    monthSpendDataCAD: [],
  },
  orgStats: {
    loading: false,
  },
  transactionsSnapshotPaginatedData:
    initialTransactionsSnapshotPaginatedDataState,
}

export type Actions =
  | TransactionsSnapshotPaginatedDataActions
  | GetTotalAvailableCreditActions
  | GetTotalAvailableCreditV2Actions
  | GetMonthlySpendActions
  | GetSixMonthSpendActions
  | GetSixMonthSpendByCurrencyActions
  | GetOrgStatsActions

const CoreHomeReducer = (state = initialState, action: Actions): HomeState => ({
  ...produce(state, (draft) => {
    switch (action.type) {
      case GET_TOTAL_AVAILABLE_CREDIT_INIT:
      case GET_TOTAL_AVAILABLE_CREDIT_V2_INIT: {
        draft.totalAvailableCredit.loading = true
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_SUCCESS: {
        draft.totalAvailableCredit.loading = false
        draft.totalAvailableCredit.totalAvailableCreditCents =
          action.payload.totalAvailableCreditCents
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS: {
        draft.totalAvailableCredit.loading = false
        draft.totalAvailableCredit.totalAvailableCreditCentsUSD =
          action.payload.totalAvailableCreditCentsUSD
        draft.totalAvailableCredit.totalAvailableCreditCentsCAD =
          action.payload.totalAvailableCreditCentsCAD
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_FAILURE:
      case GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE: {
        draft.totalAvailableCredit.loading = false
        return draft
      }

      case GET_MONTHLY_SPEND_INIT: {
        draft.monthlySpend.loading = true
        draft.monthlySpend.topLevelError = undefined
        return draft
      }

      case GET_MONTHLY_SPEND_SUCCESS: {
        draft.monthlySpend.loading = false
        draft.monthlySpend.dates = Array.from(
          new Set([
            ...draft.monthlySpend.dates,
            // TODO: eslint tech debt
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...action.payload.chartData.dailySpendData?.map(
              (dailySpend) => dailySpend.date,
            ),
          ]),
        )
        draft.monthlySpend.organizationId =
          action.payload.chartData.organizationId
        return draft
      }

      case GET_MONTHLY_SPEND_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.monthlySpend.loading = false
        draft.monthlySpend.topLevelError = errors.topLevelError
        return draft
      }

      case GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT:
      case GET_SIX_MONTH_SPEND_INIT: {
        draft.sixMonthSpend.loading = true
        draft.sixMonthSpend.topLevelError = undefined
        return draft
      }

      case GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS: {
        draft.sixMonthSpend.loading = false
        draft.sixMonthSpend.organizationId = action.payload.organizationId
        draft.sixMonthSpend.lastUpdatedAt = action.payload.lastUpdatedAt
        draft.sixMonthSpend.monthSpendDataUSD = action.payload.monthSpendDataUSD
        draft.sixMonthSpend.monthSpendDataCAD = action.payload.monthSpendDataCAD
        return draft
      }

      case GET_SIX_MONTH_SPEND_SUCCESS: {
        draft.sixMonthSpend.loading = false
        draft.sixMonthSpend.organizationId = action.payload.organizationId
        draft.sixMonthSpend.lastUpdatedAt = action.payload.lastUpdatedAt
        draft.sixMonthSpend.monthSpendData = action.payload.monthSpendData
        return draft
      }

      case GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE:
      case GET_SIX_MONTH_SPEND_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.sixMonthSpend.loading = false
        draft.sixMonthSpend.topLevelError = errors.topLevelError
        return draft
      }

      case GET_ORG_STATS_INIT: {
        draft.orgStats.loading = true
        draft.orgStats.topLevelError = undefined
        return draft
      }

      case GET_ORG_STATS_SUCCESS: {
        draft.orgStats.loading = false
        draft.orgStats.total = action.payload.total
        draft.orgStats.owner = action.payload.owner
        draft.orgStats.admin = action.payload.admin
        draft.orgStats.cardManager = action.payload.cardManager
        draft.orgStats.bookkeeper = action.payload.bookkeeper
        draft.orgStats.member = action.payload.member
        draft.orgStats.guest = action.payload.guest
        return draft
      }

      case GET_ORG_STATS_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.orgStats.loading = false
        draft.orgStats.topLevelError = errors.topLevelError
        return draft
      }
    }
  }),
  transactionsSnapshotPaginatedData: TransactionsSnapshotPaginatedDataReducer(
    state.transactionsSnapshotPaginatedData,
    action as TransactionsSnapshotPaginatedDataActions,
  ),
})

const HomeReducer = createBackgroundReducer(
  homeRouteProps,
  initialState,
  CoreHomeReducer,
)

export default HomeReducer
